import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import {
  AttachmentDownloadUrlResponse,
  ChatAttachmentCreate,
  ChatMetadata,
  ClienteChatMessage,
  ClienteChatMessagesResponse,
  ClienteMarkReadResponse,
  ClienteSendMessageRequest,
  ClienteUnreadCountResponse,
  buildMessageType,
  hasValidChatContent
} from '../models/chat';

@Injectable({
  providedIn: 'root'
})
export class ClienteChatService {
  constructor(private apiUrl: ApiUrlService, private http: HttpClient) {}

  public obtenerNoLeidos(): Observable<ClienteUnreadCountResponse> {
    return this.http.get<ClienteUnreadCountResponse>(this.apiUrl.clienteChatUnreadCount);
  }

  public obtenerMensajes(limit = 30, cursorId?: number | null): Observable<ClienteChatMessagesResponse> {
    let params = new HttpParams().set('limit', String(limit));

    if (cursorId !== null && cursorId !== undefined) {
      params = params.set('cursor_id', String(cursorId));
    }

    return this.http.get<ClienteChatMessagesResponse>(this.apiUrl.clienteChatMessages, { params });
  }

  public enviarMensaje(payload: ClienteSendMessageRequest): Observable<ClienteChatMessage> {
    return this.http.post<ClienteChatMessage>(this.apiUrl.clienteChatMessages, payload);
  }

  public enviarMensajeConAdjuntos(
    files: File[],
    bodyText?: string | null,
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): Observable<ClienteChatMessage> {
    const formData = this.crearFormDataAdjuntos(files, bodyText, idRepliedMessage, metadataJson);
    return this.http.post<ClienteChatMessage>(this.apiUrl.clienteChatMessagesWithAttachments, formData);
  }

  public enviarTexto(
    bodyText: string,
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): Observable<ClienteChatMessage> {
    const payload = this.crearPayloadMensaje(bodyText, [], idRepliedMessage, metadataJson);
    return this.enviarMensaje(payload);
  }

  public marcarConversacionLeida(): Observable<ClienteMarkReadResponse> {
    return this.http.post<ClienteMarkReadResponse>(this.apiUrl.clienteChatRead, null);
  }

  public obtenerUrlDescargaAdjunto(idAttachment: number): Observable<AttachmentDownloadUrlResponse> {
    return this.http.get<AttachmentDownloadUrlResponse>(
      this.apiUrl.clienteChatAttachmentDownloadUrl(idAttachment)
    );
  }

  public obtenerContenidoAdjunto(idAttachment: number): Observable<Blob> {
    return this.http.get(this.apiUrl.clienteChatAttachmentContent(idAttachment), {
      responseType: 'blob'
    });
  }

  public crearPayloadMensaje(
    bodyText?: string | null,
    attachments: ChatAttachmentCreate[] = [],
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): ClienteSendMessageRequest {
    const normalizedText = bodyText?.trim() || null;

    if (!hasValidChatContent(normalizedText, attachments.length)) {
      throw new Error('El mensaje debe incluir texto o adjuntos ya subidos.');
    }

    return {
      body_text: normalizedText,
      message_type: buildMessageType(normalizedText, attachments.length),
      attachments,
      id_replied_message: idRepliedMessage ?? null,
      metadata_json: metadataJson ?? null
    };
  }

  public esPayloadMensajeValido(payload: ClienteSendMessageRequest): boolean {
    return hasValidChatContent(payload.body_text, payload.attachments.length);
  }

  private crearFormDataAdjuntos(
    files: File[],
    bodyText?: string | null,
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): FormData {
    const formData = new FormData();
    const normalizedText = bodyText?.trim();

    if (normalizedText) {
      formData.append('body_text', normalizedText);
    }

    if (idRepliedMessage !== null && idRepliedMessage !== undefined) {
      formData.append('id_replied_message', String(idRepliedMessage));
    }

    if (metadataJson) {
      formData.append('metadata_json', JSON.stringify(metadataJson));
    }

    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return formData;
  }
}
